import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const PublicationSubmissions = () => {
  const user = useSelector((state) => state.auth.user);
  const userDisplayName = user?.name || user?.email || "Admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("publication-submissions/all", {
        params: { status: filter },
      });
      setItems(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching publication submissions:", error);
      Swal.fire("Error", "Failed to load publication submissions", "error");
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (selected) {
      setAdminNote(selected.adminNote || "");
    }
  }, [selected]);

  const openItem = (item) => {
    setSelected(item);
    setAdminNote(item.adminNote || "");
  };

  const closeDetail = () => {
    setSelected(null);
    setAdminNote("");
  };

  const setStatus = async (status, successMessage) => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.put(
        `publication-submissions/change/${selected._id}`,
        { status, adminNote }
      );
      Swal.fire("Success", successMessage, "success");
      setSelected(response.data?.data || null);
      fetchItems();
      if (filter === "pending" && status !== "pending") {
        closeDetail();
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update submission";
      Swal.fire("Error", message, "error");
    }
    setActionLoading(false);
  };

  const deleteItem = async (id) => {
    const result = await Swal.fire({
      title: "Delete this submission?",
      text: "If approved, the published research entry will also be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      await axiosInstance.delete(`publication-submissions/delete/${id}`);
      Swal.fire("Deleted", "Submission removed", "success");
      if (selected?._id === id) closeDetail();
      fetchItems();
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete submission";
      Swal.fire("Error", message, "error");
    }
    setActionLoading(false);
  };

  const filterBtn = (value, label) => (
    <button
      key={value}
      type="button"
      onClick={() => {
        setFilter(value);
        closeDetail();
      }}
      style={{
        padding: "10px 20px",
        marginRight: "10px",
        backgroundColor: filter === value ? "#4CAF50" : "#ccc",
        color: "#fff",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        textTransform: "capitalize",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ padding: "8px 0" }}>
      <h2 style={{ marginTop: 0, marginBottom: 16 }}>
        Publication Submissions
      </h2>
      <p style={{ color: "#666", marginTop: 0, marginBottom: 20 }}>
        Review research publications submitted from the Directorate of IT
        Services page. Approving publishes them to ORIC → Research Publications.
      </p>

      <div style={{ marginBottom: 20 }}>
        {filterBtn("pending", "Pending")}
        {filterBtn("approved", "Approved")}
        {filterBtn("rejected", "Rejected")}
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: selected ? "1fr 1fr" : "1fr",
            gap: 20,
            alignItems: "start",
          }}
        >
          <div>
            {items.length === 0 ? (
              <p style={{ color: "#666" }}>No {filter} submissions.</p>
            ) : (
              items.map((item) => (
                <div
                  key={item._id}
                  onClick={() => openItem(item)}
                  style={{
                    border:
                      selected?._id === item._id
                        ? "2px solid #4CAF50"
                        : "1px solid #e0e0e0",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 12,
                    cursor: "pointer",
                    background:
                      selected?._id === item._id ? "#f1f8f1" : "#fff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      marginBottom: 6,
                    }}
                  >
                    <strong>{item.title}</strong>
                    <span style={{ fontSize: 12, color: "#888" }}>
                      {formatDate(item.createdAt)}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: "#555" }}>
                    {item.name} · {item.email}
                  </div>
                  {item.authors && (
                    <div style={{ marginTop: 6, fontSize: 13, color: "#666" }}>
                      Authors: {item.authors}
                    </div>
                  )}
                  <div style={{ marginTop: 6, fontSize: 13, color: "#666" }}>
                    {(item.description || "").slice(0, 100)}
                    {(item.description || "").length > 100 ? "…" : ""}
                  </div>
                  {item.respondedBy && (
                    <div
                      style={{
                        marginTop: 8,
                        fontSize: 12,
                        color: "#4CAF50",
                      }}
                    >
                      {item.status === "approved" ? "Approved" : "Rejected"} by{" "}
                      {item.respondedBy}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {selected && (
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 8,
                padding: 20,
                background: "#fafafa",
                position: "sticky",
                top: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <h3 style={{ margin: 0 }}>Submission details</h3>
                <button
                  type="button"
                  onClick={closeDetail}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: 18,
                  }}
                >
                  ×
                </button>
              </div>

              <p>
                <strong>Title:</strong> {selected.title}
              </p>
              <p>
                <strong>Authors:</strong> {selected.authors || "—"}
              </p>
              <p>
                <strong>Submitted by:</strong> {selected.name}
              </p>
              <p>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${selected.email}`}>{selected.email}</a>
              </p>
              <p>
                <strong>Phone:</strong>{" "}
                {selected.phone ? (
                  <a href={`tel:${selected.phone}`}>{selected.phone}</a>
                ) : (
                  "—"
                )}
              </p>
              <p>
                <strong>Designation:</strong> {selected.designation || "—"}
              </p>
              <p>
                <strong>Department:</strong> {selected.department || "—"}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <span style={{ textTransform: "capitalize" }}>
                  {selected.status}
                </span>
              </p>
              <p>
                <strong>Received:</strong> {formatDate(selected.createdAt)}
              </p>
              {selected.respondedAt && (
                <p>
                  <strong>Reviewed:</strong>{" "}
                  {formatDate(selected.respondedAt)}
                  {selected.respondedBy ? ` by ${selected.respondedBy}` : ""}
                </p>
              )}
              {selected.researchId && (
                <p>
                  <strong>Published research ID:</strong> {selected.researchId}
                </p>
              )}
              <p>
                <strong>Description:</strong>
              </p>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 16,
                }}
              >
                {selected.description}
              </div>

              {selected.fileUrl && (
                <p style={{ marginBottom: 16 }}>
                  <strong>Attachment:</strong>{" "}
                  <a
                    href={selected.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View file
                  </a>
                </p>
              )}

              <label
                htmlFor="pubAdminNote"
                style={{ display: "block", fontWeight: 600, marginBottom: 6 }}
              >
                {(selected.respondedBy || userDisplayName)} note
              </label>
              <textarea
                id="pubAdminNote"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 10,
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  marginBottom: 16,
                }}
                placeholder={`Add a note as ${userDisplayName}…`}
              />

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {selected.status !== "approved" && (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() =>
                      setStatus(
                        "approved",
                        "Approved and published to Research Publications"
                      )
                    }
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "#4CAF50",
                      color: "#fff",
                      border: "none",
                      borderRadius: 5,
                      cursor: "pointer",
                    }}
                  >
                    {actionLoading ? "Saving…" : "Approve & Publish"}
                  </button>
                )}
                {selected.status !== "rejected" && (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => setStatus("rejected", "Marked as rejected")}
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "#FF9800",
                      color: "#fff",
                      border: "none",
                      borderRadius: 5,
                      cursor: "pointer",
                    }}
                  >
                    {actionLoading ? "Saving…" : "Reject"}
                  </button>
                )}
                {selected.status !== "pending" && (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => setStatus("pending", "Moved back to pending")}
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "#2196F3",
                      color: "#fff",
                      border: "none",
                      borderRadius: 5,
                      cursor: "pointer",
                    }}
                  >
                    {actionLoading ? "Saving…" : "Move to Pending"}
                  </button>
                )}
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => deleteItem(selected._id)}
                  style={{
                    padding: "10px 16px",
                    backgroundColor: "#f44336",
                    color: "#fff",
                    border: "none",
                    borderRadius: 5,
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicationSubmissions;
